// packages/charts/src/react/resolveCssVars.ts
//
// ECharts' canvas renderer does NOT resolve CSS custom properties at
// draw time — `fillStyle = "var(--…)"` is an invalid color string, the
// previous valid colour is kept, and the series either stacks up in
// one colour or falls through to the `aria.decal` patterns. SVG
// renderer would be fine, but we use canvas for perf.
//
// This helper reads the resolved colours from the document root once,
// when the chart mounts. `getComputedStyle(document.documentElement)`
// returns the live value including any `:root.dark` override that's
// in effect. The LineChart / BarChart / PieChart / StackedBarChart /
// FunnelChart wrappers call `resolvePalette(vars, fallbacks)` before
// passing the `color: [...]` array to ECharts.

/**
 * Read `--varName` from `:root`. Returns the passed fallback when:
 *   - there is no document (SSR / older jsdom in tests)
 *   - the custom property is not declared
 *   - declared value is empty (``) or literally `var(…)` (unresolved)
 *
 * Returned values are trimmed. Never throws.
 */
export function resolveCssVar(varName: string, fallback: string): string {
  if (typeof document === "undefined" || !document.documentElement) {
    return fallback;
  }
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(
      varName,
    );
    const trimmed = raw.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith("var(")) return fallback;
    return trimmed;
  } catch {
    return fallback;
  }
}

/**
 * Batch form for a palette. `vars[i]` is resolved against `fallbacks[i]`.
 * Arrays must be the same length — caller invariant.
 */
/** The brand's accent replaces the FIRST entry of a resolved palette — the
 * token model's own convention (`--verity-chart-*-palette-1` is
 * `--verity-color-primary` in tokens.css). Explicit because the report
 * editor's canvas shares `document.documentElement` with the host app and
 * must not repaint its tokens (plan 2026-09-13, Besluit 9). */
export function withAccent(palette: string[], accent?: string): string[] {
  if (!accent || palette.length === 0) return palette;
  return [accent, ...palette.slice(1)];
}

export function resolvePalette(
  vars: readonly string[],
  fallbacks: readonly string[],
): string[] {
  const out: string[] = [];
  for (let i = 0; i < vars.length; i++) {
    out.push(resolveCssVar(vars[i], fallbacks[i] ?? "#3455D3"));
  }
  return out;
}
